import type { AppStoreAudioCaptureStore } from "@/store/audio-capture/AppStoreAudioCaptureStore";

import { DesktopCaptureService } from "./desktop-capture-service";
import { DesktopTranscriptionManager } from "@/store/audio-capture/transcription/managers/desktop-transcription-manager";
import { DesktopPcmProbe } from "../visualizers/desktop-pcm-probe";
import { DESKTOP_PCM_SAMPLE_RATE } from "./desktop-pcm-persistence";

import { RecordingStatus } from "@/store/audio-capture/models/recording-status";

import { runInAction } from "mobx";

import { toastModule } from "@/modules/toast";
import { objectModule } from "@/modules/object";
import { logger } from "@/modules/logger";
import { DESKTOP_CAPTURE_LABEL } from "./desktop-constants";

export class DesktopRecorderOrchestrator {
  private store: AppStoreAudioCaptureStore;
  private service: DesktopCaptureService | null = null;
  private flushTimer: NodeJS.Timeout | null = null;
  private probe: DesktopPcmProbe | null = null;

  constructor(store: AppStoreAudioCaptureStore) {
    this.store = store;
  }

  async start(noteId: string): Promise<void> {
    if (this.service) {
      try {
        await this.service.cancel();
      } catch (error) {
        logger.warn({
          message: "[DesktopRecorderOrchestrator] failed to cancel existing session before start",
          info: { error: objectModule.safeErrorAsJson(error as Error) },
        });
      } finally {
        this.service = null;
      }
    }

    runInAction(() => {
      const clearTranscript = this.store.status !== RecordingStatus.LOCKED;
      this.store.transcriptions = [
        new DesktopTranscriptionManager(0),
        new DesktopTranscriptionManager(1),
      ];
      this.store.transcription.initializeTranscriptions({ clearTranscript });
      this.store.beginRecordingSession({
        noteId,
        engine: "desktop",
        isRecordingWithoutTranscription: false,
        clearExisting: clearTranscript,
      });
      this.store.status = RecordingStatus.INITIALIZING;
      this.store.activeNoteId = noteId;
      this.store.error = null;
      this.store.isMicrophoneDisconnected = false;
      this.store.isRecordingWithoutTranscription = false;

      const probe = new DesktopPcmProbe();
      this.probe = probe;
      this.store.visualizer?.dispose();
      this.store.visualizer = probe;
      this.store.activeAudioInputDevices = [];
      this.store.activeAudioInputDeviceLabel = DESKTOP_CAPTURE_LABEL;
    });

    try {
      const service = new DesktopCaptureService({
        onTranscript: (channel, turn) => {
          const manager = this.store.transcriptions[channel];
          manager?.ingestTurn?.(turn);
          this.store.observability.recordTranscript({
            channel,
            wordCount: turn.words?.length,
            transcriptLength: turn.transcript?.length,
          });
          if (this.store.activeNoteId)
            void this.store.persistence.appendTranscript({
              noteId: this.store.activeNoteId,
              recordingId: this.store.currentSession?.recordingId,
              transcript: turn,
              channel,
            });
        },
        onPcm: samples => {
          this.probe?.pushSamples(samples);
          this.store.observability.ingestDesktopSamples(samples);
        },
      });
      this.service = service;
      await service.prepare(noteId, this.store.capturedAudioId);

      await service.start();
      const finalizeRecording = () => {
        if (this.service !== service) return;

        runInAction(() => {
          this.store.transcription.start();
          this.store.status = RecordingStatus.RECORDING;
          const now = Date.now();
          this.store.session.setStartTime({ timestamp: now });
          this.store.session.resetTotalPausedTime();
          this.store.session.resetPausedTime();
          this.store.session.startDurationInterval();
        });

        this.startFlushTimer();
      };

      this.store.startDesktopInitializationBuffer(finalizeRecording);
    } catch (error) {
      toastModule.triggerToast({
        content: "Failed to start recording",
        toastId: this.store.capturedAudioId,
      });

      logger.error({
        message: "[DesktopRecorderOrchestrator] start",
        info: { error: objectModule.safeErrorAsJson(error as Error) },
      });

      this.store.resetRecording();
      throw new Error(this.store.START_CANCELLED_ERROR_MESSAGE);
    }
  }

  async pause(): Promise<boolean> {
    try {
      if (this.store.status === RecordingStatus.PAUSED) return true;
      if (this.store.status !== RecordingStatus.RECORDING) {
        toastModule.triggerToast({
          content: "Cannot pause recording when not recording",
          toastId: this.store.capturedAudioId,
        });
        return false;
      }
      await this.service?.pause();
      this.store.transcription.pause();
      runInAction(() => {
        this.store.status = RecordingStatus.PAUSED;
        this.store.session.setPausedTime({ timestamp: Date.now() });
      });
      this.store.session.clearDurationInterval();
      await this.flushNow();
      this.stopFlushTimer();
      return true;
    } catch (error) {
      logger.error({
        message: "[DesktopRecorderOrchestrator] pause",
        info: { error: objectModule.safeErrorAsJson(error as Error) },
      });
      return false;
    }
  }

  async resume(): Promise<boolean> {
    try {
      if (this.store.status !== RecordingStatus.PAUSED) {
        toastModule.triggerToast({
          content: "Cannot resume recording when not paused",
          toastId: this.store.capturedAudioId,
        });
        return false;
      }
      await this.service?.resume();
      this.store.transcription.resume();
      runInAction(() => {
        this.store.status = RecordingStatus.RECORDING;
        this.store.session.incrementTotalPausedTime({
          delta: Date.now() - this.store.pausedTime,
        });
        this.store.session.startDurationInterval();
        this.store.isMicrophoneDisconnected = false;
      });
      this.startFlushTimer();
      return true;
    } catch (error) {
      logger.error({
        message: "[DesktopRecorderOrchestrator] resume",
        info: { error: objectModule.safeErrorAsJson(error as Error) },
      });
      return false;
    }
  }

  async stop(): Promise<void> {
    try {
      if (![RecordingStatus.RECORDING, RecordingStatus.PAUSED].includes(this.store.status)) return;
      this.store.clearDesktopInitializationBuffer();
      runInAction(() => (this.store.status = RecordingStatus.STOPPING));
      // Pause the service first to minimize any in-flight audio events
      await this.service?.pause();
      this.stopFlushTimer();
      await this.flushNow();
      const noteId = this.store.activeNoteId;
      let wavBlob: Blob | null = null;

      if (noteId) {
        const blobs = await this.store.persistence.getAudioBlobs({
          noteId,
          recordingId: this.store.currentSession?.recordingId,
        });
        // Filter to desktop PCM blobs; if none, fall back to service finalization
        const { isDesktopPcmBlob } = await import("./desktop-pcm-persistence");
        const pcmChunks = blobs.filter(isDesktopPcmBlob);
        if (pcmChunks.length > 0) {
          const { DesktopAudioAssembler } = await import("./desktop-audio-assembler");
          const tail = this.service?.getPendingPcmBlob() ?? null;
          wavBlob = await DesktopAudioAssembler.createWavFromPcmBlobs(
            pcmChunks,
            DESKTOP_PCM_SAMPLE_RATE,
            undefined,
            tail ?? undefined
          );
        }
      }
      const serviceBlob = await this.service?.stop();
      if (!wavBlob && serviceBlob) {
        wavBlob = serviceBlob;
      }

      runInAction(() => {
        if (wavBlob) {
          this.store.audioBlob = wavBlob;
          this.store.audioUrl = URL.createObjectURL(wavBlob);
        }
        this.store.session.clearDurationInterval();
        this.store.transcription.stop();
      });
    } catch (error) {
      logger.error({
        message: "[DesktopRecorderOrchestrator] stop",
        info: { error: objectModule.safeErrorAsJson(error as Error) },
      });
    } finally {
      this.service = null;
      if (this.probe) {
        const probe = this.probe;
        probe.dispose();
        this.probe = null;
        runInAction(() => {
          if (this.store.visualizer === probe) {
            this.store.visualizer = null;
          }
        });
      }
      this.store.releaseRecordingLock?.(false);

      runInAction(() => {
        this.store.releaseRecordingLock = null;
        if (this.store.status === RecordingStatus.STOPPING)
          this.store.status = RecordingStatus.STOPPED;
      });
    }
  }

  async dispose(): Promise<void> {
    if (this.service) {
      try {
        await this.service.cancel();
      } catch (error) {
        logger.warn({
          message: "[DesktopRecorderOrchestrator] dispose cancel failed",
          info: { error: objectModule.safeErrorAsJson(error as Error) },
        });
      } finally {
        this.service = null;
      }
    }

    this.stopFlushTimer();
    if (this.probe) {
      const probe = this.probe;
      probe.dispose();
      this.probe = null;
      runInAction(() => {
        if (this.store.visualizer === probe) {
          this.store.visualizer = null;
        }
      });
    }
  }

  private startFlushTimer(intervalMs = 3000) {
    this.stopFlushTimer();

    this.flushTimer = setInterval(() => {
      this.flushNow().catch(() => undefined);
    }, intervalMs);
  }

  private stopFlushTimer() {
    if (this.flushTimer !== null) {
      clearInterval(this.flushTimer as unknown as number);
      this.flushTimer = null;
    }
  }

  private async flushNow() {
    try {
      if (!this.store.activeNoteId || !this.service) return;
      const blob: Blob | null = this.service?.flushPendingPcmBlob() ?? null;
      if (blob && blob.size > 0) {
        await this.store.persistence.appendAudioBlob({
          noteId: this.store.activeNoteId,
          recordingId: this.store.currentSession?.recordingId,
          blob,
        });
      }
    } catch (_) {
      // swallow; flushing is best-effort
    }
  }
}
